import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import StarRating from "../components/StarRating";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "../components/ui/select";
import { CheckCircle2, Star } from "lucide-react";
import { COMPANY_LIST } from "../data/companies";

export default function SubmitReviewPage() {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [company, setCompany] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  // Group companies by category for the dropdown
  const grouped = COMPANY_LIST.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});
  const categories = Object.keys(grouped).sort();

  const submit = (e) => {
    e.preventDefault();
    if (!title || !body || !rating || !company) return;
    const draft = { company, rating, title, body, pros, cons, createdAt: new Date().toISOString() };
    const existing = JSON.parse(localStorage.getItem("reviewuk_drafts") || "[]");
    localStorage.setItem("reviewuk_drafts", JSON.stringify([draft, ...existing]));
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-[#f5f7f8] font-[Inter,system-ui,-apple-system,sans-serif] flex flex-col">
      <Header />
      <Breadcrumb trail={[{ label: "Home", to: "/" }, { label: "Submit a Review" }]}/>

      <section className="bg-gradient-to-b from-[#007a8a] to-[#005f6b] text-white">
        <div className="max-w-[1200px] mx-auto px-4 py-7 md:py-10">
          <h1 className="text-2xl md:text-4xl font-bold">Submit a Review</h1>
          <p className="mt-2 text-white/85 max-w-2xl text-sm md:text-base">
            Share your experience with a UK company. Honest, detailed reviews help thousands of other consumers each month.
          </p>
        </div>
      </section>

      <main className="flex-1">
        <div className="max-w-[900px] mx-auto px-4 py-6 md:py-8">
          {done ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <CheckCircle2 size={56} className="text-[#1e8e3e] mx-auto mb-3"/>
              <h2 className="text-2xl font-bold text-gray-900">Thanks for your review!</h2>
              <p className="text-gray-600 mt-2">It's been saved as a draft and will appear once moderation is complete (mocked locally).</p>
              <div className="mt-5 flex justify-center gap-2 flex-wrap">
                <button
                  onClick={() => { setDone(false); setTitle(""); setBody(""); setPros(""); setCons(""); setRating(0); setCompany(""); }}
                  className="px-5 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm font-medium"
                >
                  Submit another
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="px-5 py-2 rounded-md bg-[#e84e1c] hover:bg-[#d04515] text-white text-sm font-semibold"
                >
                  Back to companies
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-5 md:p-8 space-y-5">
              <div>
                <label className="text-sm font-semibold text-gray-800">Company</label>
                <p className="text-xs text-gray-500 mt-0.5 mb-1.5">Pick the UK company your review is about.</p>
                <Select value={company} onValueChange={setCompany}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a company…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    {categories.map((cat) => (
                      <SelectGroup key={cat}>
                        <SelectLabel>{cat}</SelectLabel>
                        {grouped[cat]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((c) => (
                            <SelectItem key={c.slug} value={c.name}>
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                                  style={{ backgroundColor: c.color }}
                                >
                                  {c.initials.slice(0, 2)}
                                </span>
                                {c.name}
                              </span>
                            </SelectItem>
                          ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-800">Your rating</label>
                <div className="mt-2 flex gap-1 items-center flex-wrap">
                  {[1,2,3,4,5].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onMouseEnter={() => setHover(s)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(s)}
                      className="p-1"
                      aria-label={`${s} stars`}
                    >
                      <Star
                        size={32}
                        className={(hover || rating) >= s ? "text-[#f5a623]" : "text-gray-300"}
                        fill="currentColor"
                        strokeWidth={0}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="ml-2 self-center text-sm text-gray-600 inline-flex items-center gap-1">
                      <StarRating value={rating} size={14}/> ({rating}.0)
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-800">Review title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="mt-1.5 w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#007a8a] focus:ring-2 focus:ring-[#007a8a]/20"
                  placeholder="Sum up your experience in one line"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-800">Your review</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                  rows={6}
                  className="mt-1.5 w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#007a8a] focus:ring-2 focus:ring-[#007a8a]/20 resize-y"
                  placeholder="Tell us what happened — be specific and helpful to other consumers."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-[#1e8e3e]">Pros (one per line)</label>
                  <textarea
                    value={pros}
                    onChange={(e) => setPros(e.target.value)}
                    rows={3}
                    className="mt-1.5 w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#1e8e3e] focus:ring-2 focus:ring-[#1e8e3e]/20 resize-y"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#e74c3c]">Cons (one per line)</label>
                  <textarea
                    value={cons}
                    onChange={(e) => setCons(e.target.value)}
                    rows={3}
                    className="mt-1.5 w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#e74c3c] focus:ring-2 focus:ring-[#e74c3c]/20 resize-y"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100 flex-wrap gap-2">
                <p className="text-xs text-gray-500">
                  Drafts are stored in your browser only (mocked). No data is sent to a server.
                </p>
                <button
                  type="submit"
                  disabled={!title || !body || !rating || !company}
                  className="bg-[#e84e1c] hover:bg-[#d04515] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-white font-semibold text-sm px-6 py-2.5 rounded-md"
                >
                  Publish review
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
